// VisualBriefAccessors.jsx
import React, { useCallback, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import useFetch from '../../hooks/useFetch';
import { useSelector } from 'react-redux';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

const VisualBriefAccessors = () => {
    const location = useLocation();
    const authState = useSelector(state => state.authReducer);
    const [fetchData, { loading }] = useFetch();
    const [vbMasterId, setVbMasterId] = useState(location.state?.vbMasterId || '');
    const [vbTitle, setVbTitle] = useState(location.state?.title || '');
    const [noOfSlides, setNoOfSlides] = useState(location.state?.noOfSlides || 0);

    // console.log("vbMasterId: ", vbMasterId);

    useEffect(() => {
        // Update state variables if location state changes
        setVbMasterId(location.state?.vbMasterId || '');
        setVbTitle(location.state?.title || '');
        setNoOfSlides(location.state?.noOfSlides || 0);
    }, [location.state]);

    const [accessors, setAccessors] = useState([]);

    // fetchAccessorsList
    const fetchAccessorsList = useCallback(() => {
        const config = {
            url: "/vbaccessors/log",
            method: "post",
            headers: { Authorization: authState.token },
            data: { vbMasterId: vbMasterId }
        };
        fetchData(config, { showSuccessToast: false })
            .then(response => {
                setAccessors(response);
            })
            .catch(error => {
                console.error("Error fetching Access Logs: ", error);
            });
    }, [authState.token, fetchData, vbMasterId]);

    useEffect(() => {
        fetchAccessorsList();
    }, [fetchAccessorsList]);

    const handleDeleteClick = (vbSlideId) => {
        if (window.confirm('Are you sure you want to delete this access log entry? This action cannot be undone.')) {
            const config = { url: `/vbaccessor/${vbSlideId}`, method: "delete", headers: { Authorization: authState.token } };
            fetchData(config).then(() => fetchAccessorsList());
        }
    };

    const formatDateTime = (date) => {
        const jsDate = new Date(date);
        const day = jsDate.getDate().toString().padStart(2, '0');
        const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
        const year = jsDate.getFullYear();
        const hours = jsDate.getHours().toString().padStart(2, '0');
        const minutes = jsDate.getMinutes().toString().padStart(2, '0');
        const seconds = jsDate.getSeconds().toString().padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
    };

    return (
        <div className="py-2 bg-black min-h-screen">
            <header className="flex flex-col items-start sm:flex-row justify-between mb-4 border border-teal-500 bg-black px-4 sm:px-6">
                <h1 className="text-lg sm:text-xl lg:text-3xl font-bold text-teal-300 py-2">VisualBrief Presentations</h1>
            </header>
            <div className="py-2 px-4 sm:py-4 sm:px-6">
                <h3 className="text-base sm:text-lg py-2 px-2 text-white bg-green-600 rounded-md mb-4">Access Log for {vbTitle}</h3>
                {loading ? (
                    <Loader />
                ) : (
                    <div>
                        {!accessors || accessors.length === 0 ? (
                            <div className="h-[40vh] sm:h-[300px] flex flex-col items-center justify-center text-yellow-300 text-center px-4">
                                No records found!
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {accessors.map((accessor, index) => (
                                    <div key={accessor._id} className="bg-gray-700 p-2 text-white rounded-md shadow-md">
                                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
                                            <div className="font-medium text-sm sm:text-base">{accessor.userName}</div>
                                            <div className="font-medium text-sm sm:text-base">{accessor.slideTitle}</div>
                                            <div className="font-medium text-xs sm:text-sm text-gray-300">{formatDateTime(accessor.dateAccessed)}</div>
                                            <div className="ml-auto mt-2 sm:mt-0 flex justify-end">
                                                <Tooltip text="Delete Access Log Entry" position="top">
                                                    <span className="text-red-500 cursor-pointer hover:text-red-700" onClick={() => handleDeleteClick(accessor._id)}>
                                                        <FontAwesomeIcon icon={faTrash} className="text-sm" />
                                                    </span>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default VisualBriefAccessors;